import type { RootState } from "~/app/store";
import type { AnyAction } from "~/app/store/types";
import type { AddToCartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { ProductService } from "~/features/addToCart/api/ProductService";
import {
  getNetPrice,
  getPackagingInformationByCode,
  getSurcharge,
} from "~/domains/product/utils/productDescriptorUtils";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import type { ThunkAction } from "redux-thunk";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import { addToCartItemsFromSubmitData } from "~/features/addToCart/redux/addToCart/addToCartItemFactory";

interface AddToCartB2CDetails {
  productCode: string;
  quantity: number;
  comment?: string;
}

interface AddToCartB2BDetails {
  productCode: string;
  packCode: string;
  quantity: number;
  priceReference: string;
  saleEnabled?: boolean;
  comment?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AddToCartSalesmanDetails extends AddToCartB2BDetails {
  // TODO
}

type AddToCartDetails = AddToCartB2CDetails | AddToCartB2BDetails | AddToCartSalesmanDetails;

export function addToCartThunk(
  data: AddToCartDetails | AddToCartDetails[],
  restrictionQuery = "",
  context: {
    page: PurchasePlace;
    recommendationPlace?: PurchasePlace;
    recommendationType?: PurchaseType;
  },
): ThunkAction<void, RootState, undefined, AnyAction> {
  return async (dispatch, getState) => {
    const state = getState();
    const items = Array.isArray(data) ? data : [data];

    const isRecommendation = !!context.recommendationType;
    const apiContext = isRecommendation
      ? {
          recommendationType: context.recommendationType,
          recommendationPlace: context.recommendationPlace,
          page: context.page,
        }
      : {
          page: context.page,
        };

    let apiItems: (AddToCartSubmitData & {
      productCode: string;
    })[];
    if (userIsCustomer()) {
      apiItems = await Promise.all(
        items.map((item) => {
          const { packCode, priceReference, productCode, quantity, saleEnabled, comment } = item as AddToCartB2BDetails;
          return ProductService.getProductDescriptor(productCode, priceReference, {
            fetch: true,
          }).promise.then((productDescriptor: ProductDescriptor) => {
            const packageInfo = getPackagingInformationByCode(productDescriptor, packCode);

            const totalQuantity =
              selectProperAmountInCart({
                productCode,
                packCode,
                priceReference,
              })(state) + quantity;

            const price = getNetPrice({ product: productDescriptor, packCode, totalQuantity });
            const surcharge = getSurcharge({ product: productDescriptor, packCode, totalQuantity });

            return {
              productCode: productDescriptor.base.hybrisCode,
              formValues: {
                selectedPackCode: packCode,
                quantity: quantity,
                comment: comment ?? "",
                priceReference: priceReference,
                optionalParts: {},
                salePriceEnabled: saleEnabled ?? false,
              },
              derived: {
                selectedPack: packageInfo,
                selectedPackaging: packageInfo
                  ? {
                      packCode,
                      rawPack: packageInfo,
                      packSize: packageInfo.packSize,
                      ean: [],
                      aliases: [],
                      countriesOfOrigin: [],
                      predecessors: [],
                      hasMixedPackSizes: false,
                    }
                  : null,
                netPrice: price,
                totalQuantity,
                descriptor: productDescriptor,
              },
              optionalParts: {},
            };
          });
        }),
      );
    } else {
      apiItems = items.map((data) => ({
        productCode: data.productCode,
        formValues: {
          quantity: data.quantity,
          priceReference: getInitialPriceReference(),
          selectedPackCode: INVALID_PACKAGE_CODE,
          optionalParts: {},
          salePriceEnabled: false,
          comment: data.comment,
        },
        derived: {
          selectedPack: null,
          selectedPackaging: null,
          netPrice: null,
          totalQuantity: 1,
          descriptor: {} as ProductDescriptor,
        },
        optionalParts: {},
      }));
    }
    void addToCart({
      items: addToCartItemsFromSubmitData({
        items: apiItems,
        restrictionQuery: restrictionQuery ?? "",
        context: apiContext,
      }),
      page: apiContext.page,
      source: "unknown",
      recommendationPlace: apiContext.recommendationPlace,
      recommendationType: apiContext.recommendationType,
    }).catch(() => undefined);
  };
}
